import {
	Box,
	Button,
	Grid,
	InputLabel,
	Stack,
	Switch,
	Typography,
	CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, useCallback, useState } from 'react';
import Swal from 'sweetalert2';
import * as yup from 'yup';
import strings from '../../Globalization';
import LoadingDialog from '../../components/common/LoadingDialog';
import consts from '../../consts';
import Model from '../../models/model';
import connectionService from '../../services/Connection';
import util from '../../util';
import { useModelForm } from '../../contexts/ModelForm';


const CustomSwitch = styled(Switch)(({ theme }) => ({
	padding: 8,
	'& .MuiSwitch-track': {
		borderRadius: 22 / 2,
		'&:before, &:after': {
			content: '""',
			position: 'absolute',
			top: '50%',
			transform: 'translateY(-50%)',
			width: 16,
			height: 16
		},
		'&:before': {
			backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
				theme.palette.getContrastText(theme.palette.primary.main)
			)}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
			left: 12
		},
		'&:after': {
			backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
				theme.palette.getContrastText(theme.palette.primary.main)
			)}" d="M19,13H5V11H19V13Z" /></svg>')`,
			right: 12
		}
	},
	'& .MuiSwitch-thumb': {
		boxShadow: 'none',
		width: 16,
		height: 16,
		margin: 2
	}
}));

const customizeLoadNumberInput = ({ Input, inputProps }) => {
	const field = inputProps.field;

	const loadNumber = parseInt(field.replace('LoadNumberSegment', ''));
	const formattedLabel = formatLoadNumberString(loadNumber);
	inputProps.fieldLabel = formattedLabel;

	return <Grid container>
		<Grid size={12}>
			<InputLabel >{formattedLabel}</InputLabel>
		</Grid>
		<Grid size={12}>
			<Input {...inputProps} />
		</Grid>
	</Grid>;
};

const formatLoadNumberString = (loadNumber) => {
	let str = strings?.LoadNumberSegment || 'Load Number Segment {0}';
	if (str) {
		str = str.replaceAll('{0}', loadNumber);
	}
	return str;
};

const comboTypes = [
	{ type: 'DateFormat', loaded: true },
	{ type: 'LoadRelease', loaded: true },
	{ type: 'LoadSegment', loaded: true },
	{ type: 'TimeFormat', loaded: true },
	{ type: 'PasswordStrength', loaded: true },
	{ type: 'Topics', loaded: true },
	{ type: 'MqttQOS', loaded: true },
	{ type: 'DataPermissionTopic', loaded: true },
	{ type: 'TelemetryPermissionTopic', loaded: true },
	{ type: 'CommandPermissionTopic', loaded: true },
	{ type: 'PubCommandPermissionTopic', loaded: true },
	{ type: 'LogLevel', loaded: true },
	{ type: 'CATAPIFeature', loaded: true },
	{ type: 'DataTopics', loaded: true },
	{ type: 'SyncInterval', loaded: true },
	{ type: 'FailSafeRestartModes', loaded: true }
];

const CustomSwitchInput = ({ Input, inputProps: { field, formik, setFieldValue } }) => (
	<Box container>
		<CustomSwitch
			checked={formik.values[field]}
			onChange={(event) => setFieldValue(field, Boolean(event.target.checked))}
			name={field}
			color="success"
		/>
	</Box>
);
const catSyncAutocompleteValueFormatter = (value) => {
	let formatedValue = [];
	if (Array.isArray(value)) {
		formatedValue = value.map((entry) => entry?.id || entry).join(',');
	}
	return formatedValue;
};

const catSyncAutocompleteValueParser = (value) => {
	let formatedValue = [];
	if (typeof value === 'string') {
		formatedValue = value.split(',');
	}
	return formatedValue;
};
const LicenseFileInput = ({ Input, inputProps }) => {
	const LicenseFile = inputProps.formik?.values?.LicenseFile;
	const activeRecord = inputProps.activeRecord;
	const handleOnClickUpload = useCallback(() => {
		activeRecord?.onUploadLicence(LicenseFile);
	}, [LicenseFile]);

	return (
		<Grid container sx={{ marginBottom: (theme) => theme.spacing(1) }}>
			<Grid size={12}>
				<Button onClick={handleOnClickUpload} color="warning" variant="contained" disableElevation>
					{strings.UploadLicense || 'Upload License'}
				</Button>
			</Grid>
		</Grid>
	);
};

const TestEmailBtn = ({ Input, inputProps: { formik } }) => {
	const { SMTPHost, SMTPPort, SMTPUserName, SMTPPassword, SMTPSSL, SMTPFrom, SMTPTo } = formik.values;
	const [loading, setLoading] = useState(false);
	const handleOnClick = useCallback(() => {
		if (formik.errors.SMTPFrom || formik.errors.SMTPTo) {
			Swal.fire({ icon: 'info', title: strings.Info, text: strings.EmailAddressIsNotValid, confirmButtonText: strings.Ok, allowOutsideClick: false });
			return;
		}
		if (SMTPHost && SMTPPort && SMTPUserName && SMTPPassword && SMTPFrom && SMTPTo) {
			setLoading(true);
			const smtpConfig = {
				host: SMTPHost,
				port: Number(SMTPPort),
				secure: SMTPSSL,
				fromEmailAddress: SMTPFrom,
				toEmailAddress: SMTPTo,
				auth: {
					user: SMTPUserName,
					pass: SMTPPassword
				}
			};
			const formData = new FormData();
			formData.append('action', 'testEmail');
			formData.append('smtp', JSON.stringify(smtpConfig));
			fetch(connectionService.Config, {
				method: 'POST',
				body: formData
			})
				.then((res) => res.json())
				.then(({ success, message, info, redirectLogin }) => {
					if (success) {
						Swal.fire({
							icon: 'success',
							title: strings.Success || 'Success',
							text: strings.EmailSuccess || 'Email Success',
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
					} else {
						Swal.fire({
							icon: 'error',
							title: strings.Error || 'Error',
							text: strings.EmailFailed || 'Email Failed',
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
					}
				})
				.finally(() => setLoading(false));
		} else {
			Swal.fire({ icon: 'info', title: strings.Info, text: strings.PleaseFillAllTheFieldsFirst, confirmButtonText: strings.Ok, allowOutsideClick: false });
		}
	}, [SMTPHost, SMTPPort, SMTPUserName, SMTPPassword, SMTPSSL, SMTPFrom, SMTPTo, formik.errors]);

	return (
		<Grid container sx={{ marginBottom: (theme) => theme.spacing(1) }}>
			<Grid size={12}>
				<LoadingDialog open={loading} />
				<Button onClick={handleOnClick} color="warning" variant="contained" disableElevation>
					{strings.EmailTest}
				</Button>
			</Grid>
		</Grid>
	);
};

const TestConnectionBtn = (record) => {
	const [loading, setLoading] = useState(false);
	const { formik = {} } = useModelForm();
	let { LicensingHostWsUrl : licensingHostWsUrl = ''} = formik.values || {};
	licensingHostWsUrl = (licensingHostWsUrl && licensingHostWsUrl.endsWith('/') ? licensingHostWsUrl : `${licensingHostWsUrl}/`) || '';
	licensingHostWsUrl += consts.testConnection;

	const showTestConnection = record.inputProps.activeRecord.record.showTestConnection;

	const handleOnClick = useCallback(async () => {
		if (!licensingHostWsUrl) {
			Swal.fire({
				title: strings.Error,
				text: strings.ValidUrl || 'Please Enter URL',
				icon: 'error',
				confirmButtonText: strings.Ok,
			});
			return;
		}
		try {
			setLoading(true);
			const json = await fetch(licensingHostWsUrl, {
				method: 'GET',
				headers: {
					'Prefer': 'return=minimal'
				}
			}).then((res) => res.json());
			if (json.success && (parseFloat(json.version) >= consts.multitenancyVersion)) {
				Swal.fire({
					title: strings.Success,
					text: strings.ConnectionSuccess,
					icon: 'success',
					confirmButtonText: strings.Ok,
				});
			} else {
				Swal.fire({
					title: strings.Error,
					text: strings.ConnectionFailed,
					icon: 'error',
					confirmButtonText: strings.Ok,
				});
			}
		} catch (error) {
			Swal.fire({
				title: strings.Error,
				text: strings.ConnectionFailed,
				icon: 'error',
				confirmButtonText: strings.Ok,
			});
		}
		finally {
			setLoading(false);
		}
	}, [licensingHostWsUrl]);


	return (showTestConnection &&
		<Button
			startIcon={loading && <CircularProgress color="warning" size={18} />}
			disabled={loading}
			onClick={handleOnClick}
			color="warning"
			variant="contained"
			disableElevation
			sx = {{
				marginTop: '8px',
				height: '100%'
			}}
		>
			{strings.TestConnection || 'Test Connection'}
		</Button>
	);
};
const TestDbBtns = ({ inputProps: { formik, activeRecord } }) => {
	const [loading, setLoading] = useState(false);
	const handleOnClick = useCallback(
		(test = true) => {
			const { DbHost, DbName, DbUserName, DbPassword, ConfirmDbPassword } = formik.values;
			const DbNameRegex = /[\/. "$*<>:|?]/g;
			if (DbName.match(DbNameRegex)) {
				Swal.fire({ icon: 'info', title: strings.Info, text: consts.dbNameValidationMsg, confirmButtonText: strings.Ok, allowOutsideClick: false });
				return;
			}
			if (ConfirmDbPassword?.trim?.() !== DbPassword?.trim?.()) {
				Swal.fire({
					icon: 'info',
					title: strings.Info || 'Info',
					text: strings.PasswordNotMatch || 'Passwords do not match',
					confirmButtonText: strings.Ok,
					allowOutsideClick: false
				});
				return;
			}
			if (Boolean(DbHost?.trim?.()) && Boolean(DbName?.trim?.())) {
				setLoading(true);

				const formData = new FormData();
				formData.append('action', test ? 'testConnection' : 'saveConnection');
				formData.append('testing', test);
				formData.append('DbHost', DbHost || '');
				formData.append('DbName', DbName || '');
				formData.append('DbUserName', DbUserName || '');
				formData.append('DbPassword', DbPassword || '');
				formData.append('ConfirmDbPassword', ConfirmDbPassword || '');
				formData.append('timeout', 60000 * 10);
				formData.append('comboTypes', JSON.stringify(comboTypes));

				fetch(connectionService.Config, {
					method: 'POST',
					body: formData
				})
					.then((res) => res.json())
					.then((json) => {
						const { success, message, Success } = json;
						if (message === 'Connection_Success' || success || Success) {
							Swal.fire({ icon: 'success', titleText: strings.Success, text: strings.ConnectionSuccess, confirmButtonText: strings.Ok, allowOutsideClick: false });
							if (!test && typeof activeRecord.onDBConnected === 'function') {
								activeRecord.onDBConnected(json);
							}
						} else {
							Swal.fire({ icon: 'error', title: strings.Error, text: strings.ConnectionFailed, confirmButtonText: strings.Ok, allowOutsideClick: false });
						}
					})
					.finally(() => setLoading(false));
			} else {
				Swal.fire({ icon: 'info', title: strings.Info, text: strings.MongoDBRequired, confirmButtonText: strings.Ok, allowOutsideClick: false });
			}
		},
		[formik.values, formik.errors]
	);

	return (
		<Grid container sx={{ marginBottom: (theme) => theme.spacing(1) }}>
			<Grid size={12}>
				<LoadingDialog open={loading} />
				<Stack direction="row" spacing={2}>
					<Button
						onClick={() => handleOnClick(true)}
						color="warning"
						variant="contained"
						disableElevation
						disabled={activeRecord?.IsDBConnected}
					>
						{strings.TestConnection || 'Test Connection'}
					</Button>
					<Button
						onClick={() => handleOnClick(false)}
						color="success"
						variant="contained"
						disableElevation
						disabled={activeRecord?.IsDBConnected}
					>
						{strings.TestApply || 'Test & Apply'}
					</Button>
				</Stack>
			</Grid>
		</Grid>
	);
};

const CycleConfigNote = memo(
	({ inputProps: { fieldLabel } }) => (
		<Grid container rowSpacing={3}>
			<Grid size={12}>
				<Typography color="primary.light" sx={{ fontWeight: 'bold', marginTop: 2 }}>
					{fieldLabel}
				</Typography>
			</Grid>
		</Grid>
	),
	() => false
);

// Check if the URL is a valid CAT Instance URL
// Todo: Add more CAT instances URL's here.
const isValidCATInstanceURL = (url) => {
	const pattern = /^(https?:\/\/)?((localhost|(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}))|([a-zA-Z0-9.-]+(\.[a-zA-Z]{2,})+))(:\d+)?(\/[^\s]*)?$/;
	return pattern.test(url);
};

const RetryLoadingFilesBtn = ({ inputProps: { activeRecord } }) => {
	const { AuthorizationToken, Name } = activeRecord.record;
	const [loading, setLoading] = useState(false);
	const handleOnClick = useCallback(() => {
		if (!AuthorizationToken && !Name) {
			Swal.fire({ icon: 'info', title: strings.Info, text: strings.AuthorizationTokenName, confirmButtonText: strings.Ok, allowOutsideClick: false});
			return;
		}
		setLoading(true);
		const formData = new FormData();
		formData.append("action", 'retryFilesUpload');
		fetch(connectionService.Config, {
			method: 'POST',
			body: formData
		})
			.then((res) => res.json())
			.then(({ success, info }) => {
				if (success) {
					Swal.fire({
						icon: 'success',
						title: strings.Success || 'Success',
						text: strings[info],
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
				} else {
					Swal.fire({
						icon: 'error',
						title: strings.Error || 'Error',
						text: strings[info],
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
				}
			})
			.finally(() => setLoading(false));
	}, []);
	return (
		<Grid container sx={{ marginTop: 1 }}>
			<Grid size={12}>
				<LoadingDialog open={loading} />
				<Button onClick={handleOnClick} color="warning" variant="contained" disableElevation>
					{strings.RetryFilesLoading}
				</Button>
			</Grid>
		</Grid>
	);
};

const DiagnosticsBtn = ({ action, fieldLabel = '' }) => {
	const [loading, setLoading] = useState(false);
	const { formik } = useModelForm();
	const { UploadDiagnosticsUrl: url } = formik.values || {};

	const handleOnClick = useCallback(() => {
		if (!url) {
			Swal.fire({ icon: 'info', title: strings.Info, text: strings.EnterValidURL, confirmButtonText: strings.Ok, allowOutsideClick: false });
			return;
		}

		// Check for valid URL structure
		if (!isValidCATInstanceURL(url)) {
			Swal.fire({ icon: 'error', title: strings.Info, text: strings.InvalidURL, confirmButtonText: strings.Ok, allowOutsideClick: false });
			return;
		}

		if(!action) {
			return;
		}

		if (url) {
			setLoading(true);
			const formData = new FormData();
			formData.append('action', action);
			formData.append('UploadURL', url);
			fetch(connectionService.UploadDiagnosticsInfo, {
				method: 'POST',
				body: formData
			})
				.then((res) => res.json())
				.then(({ success, message }) => {
					if (success) {
						Swal.fire({
							icon: 'success',
							title: strings.Success || 'Success',
							text: strings[message] || message,
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
					} else {
						Swal.fire({
							icon: 'error',
							title: strings.Error || 'Error',
							text: strings[message] || message,
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
					}
				})
				.finally(() => setLoading(false));
		} else {
			Swal.fire({ icon: 'info', title: strings.Info, text: strings.PleaseInputDiagnosticsURL, confirmButtonText: strings.Ok, allowOutsideClick: false });
		}
	}, [url, action]);

	return (
		<Grid container sx={{ marginBottom: (theme) => theme.spacing(1) }}>
			<Grid size={12}>
				<LoadingDialog open={loading} />
				<Button onClick={handleOnClick} color="warning" variant="contained" disableElevation size='medium'>
					{fieldLabel}
				</Button>
			</Grid>
		</Grid>
	);
};

const UploadPathBtn = ({ Input, inputProps }) => {
	const [open, setOpen] = useState(false);

	const handleClick = async () => {
		setOpen(true);
		const { formik } = inputProps;
		const { Path: packageFile } = formik.values;
		const formData = new FormData();
		formData.append('action', 'uploadPackageFile');
		formData.append('packageFile', new Blob([packageFile]), packageFile.name);
		try {
			// uploading the package file to Config controller
			const response = await fetch(connectionService.UploadPackageFile, {
				method: 'POST',
				body: formData
			});
			const { success, message } = await response.json();
			if (!success) {
				util.showError(strings[message] || message, strings.Error, 'error');
				return;
			}
			util.showError(strings[message] || message, strings.Success, 'success', () => window.location.reload());
		} catch (err) {
			util.showError(strings['PackageFileUploadFailed'], strings.Error, 'error');
		} finally {
			setOpen(false);
		}
	};

	return (
		<Grid container sx={{ marginBottom: (theme) => theme.spacing(1) }}>
			<Grid size={12}>
				<LoadingDialog open={open} />
				<Button color="warning" variant="contained" disableElevation onClick={handleClick}>
					{strings.Upload || 'Upload'}
				</Button>
			</Grid>
		</Grid>
	);
};

const model = new Model({
	name: 'AppConfig',
	sortable: false,
	disableAdd: true,
	disableDelete: true,
	disableExport: true,
	combos: comboTypes,
	initialValues: {
		CaBundlePath: '',
		UploadDiagnosticsUrl: import.meta.env.VITE_UPLOAD_DIAGNOSTICS_URL || 'https://dev.steriscat.com/'
	},
	formGroupsComponent: 'accordions',
	onFormLoad: ({ formik, activeRecord }) => {
		formik.setFieldValue('CaBundlePath', '');
		if (formik.values.CATReconnectionTime <= 0) {
			formik.setFieldValue('CATReconnection', '1');
			formik.setFieldValue('CATReconnectionTime', 20);
		}
		const EndoScope = activeRecord?.record?.Modules?.EndoScope || '0';
		formik.setFieldValue('EndoScope', EndoScope);
		if (activeRecord?.inSetupS) {
			formik.setFieldValue('Password', '');
			formik.setFieldValue('ConfirmUserPassword', '');
		}

		formik.setFieldValue('logsPurgeDayCount', parseInt(activeRecord?.record?.MaxLogsInDays ?? 0));
	},
	customizeSaveData: (values, { activeRecord }) => {
		let clientId = 0;
		if (util.localData?.loginData?.tags?.ClientId) {
			clientId = util.localData?.loginData?.tags?.ClientId;
		}
		if (values.LicenseFile === undefined) {
			delete values.LicenseFile;
		}
		const EndoScope = values.EndoScope;
		const IsCleanupService = values.IsCleanupService ? values.IsCleanupService : 0;
		delete values.LicenseFileBtn;
		delete values.EndoScope;
		delete values.Path;
		return {
			...values,
			_id: activeRecord.record?._id,
			id: activeRecord.record?._id,
			ClientId: clientId,
			Modules: `{"EndoScope": "${EndoScope || 0}"}`,
			IsCleanupService: IsCleanupService,
			InSetup: activeRecord.inSetup 
		};
	},
	formGroups: [
		{
			header: 'LicenseUpload',
			fields: ['LicenseFile', 'LicenseFileBtn', 'LicensingHostWsUrl', 'TestConnection', 'InstallationFilePath'],
			columnSize: 6,
			controlled: true,
			fieldProps: {
				inlineLabelSize: 4
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('LicenseUpload')
		},
		{
			header: 'DatabaseConfiguration',
			fields: ['DbHost', 'DbName', 'DbUserName', 'DbPassword', 'ConfirmDbPassword', 'TestDBConnectionBtn'],
			columnSize: 4,
			fieldProps: {
				inlineLabelSize: 4
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('DatabaseConfiguration')
		},
		{
			header: 'UserCreation',
			fields: ['FirstName', 'LastName', 'UserName', 'Email', 'Password', 'ConfirmUserPassword'],
			columnSize: 4,
			controlled: true,
			fieldProps: {
				inlineLabelSize: 4
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('UserCreation')
		},

		{
			header: 'ApplicationConfig',
			fields: [
				'PasswordStrength',
				'DateFormatId',
				'PasswordExpirationDays',
				'LoadReleaseId',
				'TimeFormatId',
				'PasswordExpirationNotifyDays',
				'ServiceInPath',
				'BIExpiredDays',
				'BINotificationTo',
				'IsProconnect',
				'NetworkPath',
				'FileNameFormat',
				'ReadFileInterval',
				'EquipmentFilePath',
				'LogLevel',
				'MaxLogsInDays',
				'IsAPITokenRequired',
				'Port',
				'SSLKeyFilePath',
				'SSLCertFilePath',
				'CatViewerPath',
				'EndoScope',
				'sessionTimeOutMins',
				'RunDBUpgrade',
				'CIRequiredWithBI',
				"TrackCycleCollectionPurgeDayCount",
				"SyncInterval",
				"webServerMinTls"
			],
			columnSize: 4,
			fieldProps: {
				inlineLabelSize: 5
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('ApplicationConfig')
		},
		{
			header: 'LoadSegmentConfigs',
			fields: [
				'LoadNumberSegment1',
				'LoadNumberSegment2',
				'LoadNumberSegment3',
				'LoadNumberSegment4',
				'LoadNumberSegment5',
				'LoadNumberSegment6'
			],
			columnSize: 4,
			fieldProps: {
				inlineLabelSize: 6
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('LoadSegmentConfigs')
		},
		{
			header: 'BrokerConfig',
			fields: [
				'BrokerPort',
				'BrokerServerKey',
				'BrokerServerCertificate',
				'CaBundlePath',
				'Persistance',
				'ClientInActiveInDays',
				'payloadSize',
				'BrokerLogsCollectionPurgeDayCount',
				'MaxPublishCount',
				'AutoAuthorization',
				'mqttCipherValues'
			],
			columnSize: 4,
			controlled: true,
			fieldProps: {
				inlineLabelSize: 6
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('BrokerConfig')
		},
		{
			header: 'EmailConfig',
			fields: [
				'SMTPHost',
				'SMTPPort',
				'SMTPUserName',
				'SMTPPassword',
				'SMTPFrom',
				'SMTPTo',
				'SMTPSSL',
				'TestEmailBtn'
			],
			columnSize: 4,
			fieldProps: {
				inlineLabelSize: 4
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('EmailConfig')
		},
		{
			header: 'PurgeConfig',
			fields: ['ArchiveFilePath', 'ArchiveDayCount', 'PurgeDayCount', 'MongoDBLogsPath', 'logsPurgeDayCount', 'VisionDataPurgeDayCount','AdvFilesPurgeDayCount'],
			columnSize: 4,
			fieldProps: {
				inlineLabelSize: 6
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('PurgeConfig')
		},
		{
			header: 'CATSync',
			fields: [
				'MqttHost',
				'MqttPort',
				'MqttClient',
				'MqttSubscribeTopic',
				'MqttDataPermissionTopic',
				'MqttCommandPermissionTopic',
				'MqttTelemetryPermissionTopic',
				'MqttPublishTopic',
				'MqttPubDataPermission',
				'MqttPubCommandPermissionTopic',
				'MqttQOS',
				'MqttSessionClean',
				'MacAddress',
				'CATReconnection',
				'CATReconnectionTime',
				'FailSafeRestartMode'
			],
			columnSize: 4,
			fieldProps: {
				inlineLabelSize: 4
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('CATSync')
		},
		{
			header: 'CycleConfig',
			fields: [
				'CycleApproval',
				'CycleApprovalStagesNote',
				'LeakTest',
				'FlowCheck',
				'PreWash',
				'Wash',
				'Rinse',
				'Disinfection',
				'FinalRinse'
			],
			columnSize: 12,
			fieldProps: {
				inlineLabelSize: 2
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('CycleConfig'),
			accordionProps: {
				sx: {
					'& .MuiAccordionDetails-root': {
						padding: (theme) => theme.spacing(2)
					}
				}
			},
			containerProps: {
				sx: {
					border: (theme) => `1px solid ${theme.palette.divider}`,
					borderRadius: (theme) => theme.spacing(),
					padding: (theme) => theme.spacing(2),
					'& .checkInput': {
						padding: (theme) => theme.spacing(0, 1, 0),
						'& .innerInputWrapper': {
							borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
							'&:hover': {
								backgroundColor: (theme) => theme.palette.action.hover
							},
							'& .MuiFormLabel-root': {
								fontWeight: 400
							},
							'& .MuiGrid-grid-lg-2': {
								display: 'flex',
								flexDirection: 'row-reverse',
								'& .inputContainer': {
									width: 'auto'
								}
							}
						}
					}
				},
				spacing: 1
			}
		},
		{
			header: 'CatApi',
			fields: [
				'CatService',
				'ApiDbName',
				'CatFeature',
				'CatPort',
				'IsCleanupService',
				'CleanupFrequency',
				'MaxCycleDays',
				'APIArchiveLocation',
				'ApiLogLevel',
				'ApiLogPath'
			],
			columnSize: 4,
			fieldProps: {
				inlineLabelSize: 4
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('CatApi')
		},
		{
			header: 'UploadAPIConfiguration',
			fields: ['Name', 'UploadURL', 'ContentType', 'AuthorizationToken', 'CycleXMLFile', "RetryLoadingFilesBtn"],
			columnSize: 4,
			fieldProps: {
				inlineLabelSize: 4
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('UploadAPIConfiguration')
		},
		{
			header: 'CatCloudURL',
			fields: ['CatCloudURL'],
			columnSize: 12,
			fieldProps: {
				inlineLabelSize: 2
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('CatCloudURL') && activeRecord?.isCATEdgeLicenseOnly
		},
		{
			header: 'DiagnosticsUpload',
			fields: ['UploadDiagnosticsUrl', 'DiagnosticNoOfDays', 'UploadCyclesBtn', 'UploadDiagnosticsBtn', 'SaveToLocalStorage'],
			columnSize: 12,
			fieldProps: {
				inlineLabelSize: 2
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('DiagnosticsUpload')
		},
		{
			header: 'ScopeUpdateConfig',
			fields: ['Path', 'Upload', 'CurrentVersion', 'URL', 'Time'],
			columnSize: 12,
			fieldProps: {
				inlineLabelSize: 2
			},
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('ScopeUpdate')
		}
	],
	columns: [
		{
			field: 'LicenseFile',
			label: 'LicensePath',
			accept: '.lic,.zip',
			type: 'file'
		},
		{
			field: 'InstallationFilePath',
			label: 'InstallationFilePath',
			type: 'string',
			visible: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('LicenseUpload')
		},
		{
			field: 'LicenseFileBtn',
			label: 'LicensePath',
			hideLabel: true,
			customizeInput: LicenseFileInput
		},

		{
			field: 'PasswordStrength',
			lookup: 'PasswordStrength',
			label: 'PasswordStrength',
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'DateFormatId',
			lookup: 'DateFormat',
			label: 'Date',
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ activeRecord }) => (activeRecord?.licenseType?.catexport || activeRecord?.licenseType?.catviewer)
		},
		{
			field: 'PasswordExpirationDays',
			label: 'PasswordExpirationDays',
			type: 'number',
			minValue: 0,
			maxValue: 9999,
			onChange: ({ value, formik }) => {
				if (value?.length > 4) {
					Swal.fire({
						titleText: strings.Info || 'Info',
						text: strings.PasswordExpirationAlert,
						icon: 'info',
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
				}
			}
		},
		{
			field: 'ServiceInPath',
			label: 'InServiceUrl',
			type: 'string',
			inputWhen: ({ activeRecord }) => !activeRecord?.isCATSyncLicenseOnly && !activeRecord?.isSuperVisior && (activeRecord?.licenseType?.catexport || activeRecord?.licenseType?.catviewer)
		},
		{
			field: 'LoadReleaseId',
			label: 'LoadRelease',
			lookup: 'LoadRelease',
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ activeRecord }) => {
				const { licenseType } = activeRecord;
				const isshow =
					licenseType?.products.includes('catsync') ||
					licenseType?.products.includes('catexport') ||
					licenseType?.products.includes('catendo');

				return !isshow || licenseType?.products.includes('catviewer');
			}
		},
		{
			field: 'TimeFormatId',
			header: 'Time',
			label: 'Time',
			lookup: 'TimeFormat',
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'PasswordExpirationNotifyDays',
			header: 'PasswordExpirationNotifyDays',
			label: 'PasswordExpirationNotifyDays',
			type: 'number',
			minValue: 0,
			maxValue: 9999,
			inputWhen: ({ activeRecord }) => !activeRecord?.isCATSyncLicenseOnly,
			onChange: ({ value, formik }) => {
				if (value?.length > 4) {
					Swal.fire({
						titleText: strings.Info || 'Info',
						text: strings.PasswordExpirationNotifyAlert,
						icon: 'info',
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
				}
			}
		},
		{
			field: 'BIExpiredDays',
			type: 'number',
			minValue: 0,
			inputWhen: ({ activeRecord }) => {
				const { licenseType } = activeRecord;
				const isshow =
					licenseType?.products.includes('catsync') ||
					licenseType?.products.includes('catexport') ||
					licenseType?.products.includes('catendo');
				return !isshow || licenseType?.products.includes('catviewer');
			}
		},
		{
			field: 'NetworkPath',
			header: 'NetworkPath',
			type: 'string',
			readOnly: [['IsProconnect', false]],
			required: ({ activeRecord }) => 
				activeRecord?.formGroupsVisibility?.get?.('ApplicationConfig') &&
					(!activeRecord?.licenseType?.catsync ||
						(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
					!activeRecord?.isSuperVisior && (activeRecord?.licenseType?.catexport || activeRecord?.licenseType?.catviewer)
					? [['IsProconnect', true]]
					: false,
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior && (activeRecord?.licenseType?.catexport || activeRecord?.licenseType?.catviewer)
		},
		{
			field: 'BINotificationTo',
			type: 'string',
			inputWhen: ({ activeRecord }) => {
				const { licenseType } = activeRecord;
				const isshow =
					licenseType?.products.includes('catsync') ||
					licenseType?.products.includes('catexport') ||
					licenseType?.products.includes('catendo');
				return !isshow || licenseType?.products.includes('catviewer');
			}
		},
		{
			field: 'FileNameFormat',
			type: 'string',
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior && (activeRecord?.licenseType?.catexport || activeRecord?.licenseType?.catviewer)
		},
		{
			field: 'ReadFileInterval',
			label: 'TimeIntervalReadFiles',
			type: 'number',
			minValue: 0,
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior
		},
		{
			field: 'EquipmentFilePath',
			type: 'string',
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior && (activeRecord?.licenseType?.catexport || activeRecord?.licenseType?.catviewer)
		},
		{
			field: 'LogLevel',
			lookup: 'LogLevel',
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ activeRecord }) => !activeRecord?.isSuperVisior
		},
		{
			field: 'MaxLogsInDays',
			header: 'MaxLogs',
			type: 'number',
			defaultValue: null,
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior
		},
		{
			field: 'IsProconnect',
			type: 'boolean',
			lookup: [
				{ LookupId: true, DisplayValue: 'Yes' },
				{ LookupId: false, DisplayValue: 'No' }
			],
			onChange: ({ value, formik }) => {
				if (!value) {
					formik.setFieldValue('NetworkPath', '');
				}
			},
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior && (activeRecord?.licenseType?.catexport || activeRecord?.licenseType?.catviewer)
		},
		{
			field: 'IsAPITokenRequired',
			type: 'boolean',
			lookup: [
				{ LookupId: true, DisplayValue: 'Yes' },
				{ LookupId: false, DisplayValue: 'No' }
			],
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior && (activeRecord?.licenseType?.catexport || activeRecord?.licenseType?.catviewer)
		},
		{
			field: 'Port',
			type: 'number',
			minValue: 0,
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('ApplicationConfig'),
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior
		},
		{
			field: 'SSLKeyFilePath',
			label: 'SSLKey',
			type: 'file',
			accept: '.key',
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior
		},
		{
			field: 'SSLCertFilePath',
			label: 'SSLCertificate',
			type: 'file',
			accept: '.crt',
			inputWhen: ({ activeRecord }) =>
				(!activeRecord?.licenseType?.catsync ||
					(activeRecord?.licenseType?.catsync && activeRecord?.licenseType?.multi)) &&
				!activeRecord?.isSuperVisior
		},
		{
			field: 'CatViewerPath',
			label: 'CatViewerPath',
			inputWhen: ({ activeRecord }) => {
				const { licenseType } = activeRecord;
				const isshow =
					licenseType?.products.includes('catsync') ||
					licenseType?.products.includes('catexport') ||
					licenseType?.products.includes('catendo');
				return !isshow || (licenseType?.products.includes('catviewer') && !activeRecord?.isSuperVisior);
			}
		},
		{
			field: 'EndoScope',
			lookup: [
				{ LookupId: '1', DisplayValue: 'Yes' },
				{ LookupId: '0', DisplayValue: 'No' }
			],
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ activeRecord }) => {
				const { licenseType } = activeRecord;
				const isshow =
					licenseType?.products.includes('catsync') ||
					licenseType?.products.includes('catexport') ||
					licenseType?.products.includes('catendo');
				return !isshow || (licenseType?.products.includes('catviewer') && !activeRecord?.isSuperVisior);
			}
		},
		{
			field: 'sessionTimeOutMins',
			label: 'sessionTimeOutMins',
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ activeRecord }) => {
				const { licenseType } = activeRecord;
				const hasCATExportAndEndo =
					licenseType?.products &&
					licenseType?.products.includes('catexport') &&
					licenseType?.products.includes('catendo');
				return (activeRecord?.licenseType?.catviewer && !activeRecord?.isSuperVisior) || !hasCATExportAndEndo;
			}
		},
		{
			field: 'CIRequiredWithBI',
			label: "CIRequiredWithBI",
			inputProps: {
				disableClearable: true
			},
			type: 'boolean',
			lookup: [
				{ LookupId: true, DisplayValue: 'Yes' },
				{ LookupId: false, DisplayValue: 'No' }
			],
			inputWhen: ({ activeRecord }) => {
				const { licenseType } = activeRecord;
				const isshow =
					licenseType?.products.includes('catsync') ||
					licenseType?.products.includes('catexport') ||
					licenseType?.products.includes('catendo');
				return (!isshow || licenseType?.products.includes('catviewer')) && !activeRecord?.isSuperVisior;
			}
		},
		{
			field: 'RunDBUpgrade',
			type: 'boolean',
			autocompleteValueFormatter: (value) => value?.id,
			label: 'RunDBUpgrade',
			lookup: [
				{ LookupId: true, DisplayValue: 'Yes' },
				{ LookupId: false, DisplayValue: 'No' }
			],
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ activeRecord }) => !activeRecord?.isSuperVisior
		},
		{
			field: 'LoadNumberSegment1',
			label: 'LoadNumberSegment1',
			lookup: 'LoadSegment',
			inputProps: {
				disableClearable: true
			},
			hideLabel: true,
			customizeInput: customizeLoadNumberInput
		},
		{
			field: 'LoadNumberSegment2',
			label: 'LoadNumberSegment2',
			lookup: 'LoadSegment',
			inputProps: {
				disableClearable: true
			},
			hideLabel: true,
			customizeInput: customizeLoadNumberInput
		},
		{
			field: 'LoadNumberSegment3',
			header: 'LoadNumberSegment3',
			lookup: 'LoadSegment',
			inputProps: {
				disableClearable: true
			},
			hideLabel: true,
			customizeInput: customizeLoadNumberInput
		},
		{
			field: 'LoadNumberSegment4',
			header: 'LoadNumberSegment4',
			lookup: 'LoadSegment',
			inputProps: {
				disableClearable: true
			},
			hideLabel: true,
			customizeInput: customizeLoadNumberInput
		},
		{
			field: 'LoadNumberSegment5',
			header: 'LoadNumberSegment5',
			lookup: 'LoadSegment',
			inputProps: {
				disableClearable: true
			},
			hideLabel: true,
			customizeInput: customizeLoadNumberInput
		},
		{
			field: 'LoadNumberSegment6',
			header: 'LoadNumberSegment6',
			lookup: 'LoadSegment',
			inputProps: {
				disableClearable: true
			},
			hideLabel: true,
			customizeInput: customizeLoadNumberInput
		},
		{
			field: 'BrokerPort',
			label: 'Port',
			type: 'number',
			minValue: 0
		},
		{
			field: 'BrokerServerKey',
			label: 'SecureKeyPath',
			type: 'file',
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('BrokerConfigRequired') && !activeRecord?.record.hasServerKeyFile,
			accept: '.key'
		},
		{
			field: 'BrokerServerCertificate',
			label: 'SecureCertPath',
			type: 'file',
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('BrokerConfigRequired') && !activeRecord?.record.hasServerCertFile,
			accept: '.crt,.cert'
		},
		{
			field: 'CaBundlePath',
			label: 'ca-bundle',
			type: 'file',
			accept: '.pem,.crt'
		},
		{
			field: 'Persistance',
			label: 'Persistence',
			type: 'boolean',
			lookup: [
				{ LookupId: true, DisplayValue: 'Yes' },
				{ LookupId: false, DisplayValue: 'No' }
			],
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'ClientInActiveInDays',
			header: 'ClientDeactivate',
			type: 'number',
			minValue: 0
		},
		{
			field: 'SMTPHost',
			header: 'Host'
		},
		{
			field: 'SMTPPort',
			header: 'Port',
			type: 'number',
			minValue: 0
		},
		{
			field: 'SMTPUserName',
			header: 'Username'
		},
		{
			field: 'SMTPPassword',
			header: 'Password',
			type: 'password',
			skipPasswordStrengthCheck: true,
			includeValueOnForm: true
		},
		{
			field: 'SMTPFrom',
			header: 'FromEmail',
			validate: 'isEmail'
		},
		{
			field: 'SMTPTo',
			header: 'ToEmail',
			validate: 'isEmail'
		},
		{
			field: 'SMTPSSL',
			header: 'Secure',
			type: 'boolean',
			lookup: [
				{ LookupId: true, DisplayValue: 'Yes' },
				{ LookupId: false, DisplayValue: 'No' }
			],
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'TestEmailBtn',
			header: 'EmailTest',
			hideLabel: true,
			columnSize: 12,
			customizeInput: TestEmailBtn
		}, // Form group
		{
			field: 'ArchiveFilePath',
			header: 'ArchiveFolderPath'
		},
		{
			field: 'ArchiveDayCount',
			header: 'ArchiveDayCount',
			type: 'number',
			minValue: 1,
			maxValue: 365,
			validate: yup
				.number()
				.test(
					'not-morethan-PurgeDayCount',
					`${strings.ArchiveDayCount || 'Archive Day Count'} cannot be equal to or more than ${strings.PurgeDayCount || 'Purge Day Count'
					}`,
					(val, { parent: { PurgeDayCount } }) => val < PurgeDayCount || val > 365
				),
			inputWhen: ({activeRecord}) => !activeRecord.licenseType.catviewer
		},
		{
			field: 'PurgeDayCount',
			header: 'PurgeDayCount',
			type: 'number',
			minValue: 1,
			inputWhen: ({activeRecord}) => !activeRecord.licenseType.catviewer
		},
		{
			field: 'MongoDBLogsPath',
			label: 'MongoDBLogsPath'
		},
		{
			field: 'logsPurgeDayCount',
			label: 'logsPurgeDayCount',
			type: 'number',
			defaultValue: 14,
			minValue: 1,
			// eager: true,
			onChange: ({ formik, value }) => {
				formik.setFieldValue('MaxLogsInDays', value);
			}
		},
		{
			field: 'AdvFilesPurgeDayCount',
			header: 'CycleDirectoryPurgeDayCount',
			type: 'number',
			defaultValue: 7,
			minValue: 1
		},
		{
			field: 'MqttHost',
			header: 'MqttHost'
		},
		{
			field: 'MqttPort',
			header: 'MqttPort',
			type: 'number',
			minValue: 0
		},
		{
			field: 'MqttClient',
			header: 'MqttClient'
		},
		{
			field: 'MqttSubscribeTopic',
			header: 'MqttSubscribeTopic',
			lookup: 'Topics',
			type: 'string',
			autocompleteValueFormatter: catSyncAutocompleteValueFormatter,
			multiple: true,
			inputProps: {
				disableClearable: true
			},
			autocompleteValueParser: (value) => {
				let valueToShow = value || [];
				const valuesToRemove = ['data', 'cmd'];
				if (valueToShow.length) {
					valueToShow = valueToShow.split(',')
					valueToShow = valueToShow.filter(item => !valuesToRemove.includes(item));
					valueToShow.join(',')
				}
				return valueToShow
			}
		},
		{
			field: 'MqttDataPermissionTopic',
			label: 'DataPermission',
			lookup: 'DataPermissionTopic',
			type: 'string',
			autocompleteValueFormatter: catSyncAutocompleteValueFormatter,
			autocompleteValueParser: catSyncAutocompleteValueParser,
			multiple: true,
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'MqttTelemetryPermissionTopic',
			label: 'SubTelemetryPermission',
			lookup: 'TelemetryPermissionTopic',
			type: 'string',
			autocompleteValueFormatter: catSyncAutocompleteValueFormatter,
			autocompleteValueParser: catSyncAutocompleteValueParser,
			multiple: true,
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'MqttPublishTopic',
			lookup: 'Topics',
			filterSelectedOptions: true,
			autocompleteValueFormatter: catSyncAutocompleteValueFormatter,
			multiple: true,
			type: 'string',
			autocompleteValueParser: (value, options) => {
				let valueToShow = value
				const valuesToRemove = ['data', 'cmd'];
				if (valueToShow.length) {
					valueToShow = valueToShow.split(',')
					valueToShow = valueToShow.filter(item => !valuesToRemove.includes(item));
					valueToShow.join(',')
				}
				return valueToShow
			},
			inputProps: {
				disableClearable: true
			}

		},
		{
			field: 'MqttPubDataPermission',
			lookup: 'DataTopics',
			filterSelectedOptions: true,
			type: 'string',
			autocompleteValueFormatter: catSyncAutocompleteValueFormatter,
			autocompleteValueParser: catSyncAutocompleteValueParser,
			multiple: true,
			header: 'MqttPubDataPermission',
			inputProps: {
				disableClearable: true,
				getOptionDisabled: (option) => !consts.MqttPubAllowTopics.includes(option.id)
			}
		},
		{
			field: 'MqttCommandPermissionTopic',
			header: 'CommandPermission',
			lookup: 'CommandPermissionTopic',
			type: 'string',
			filterSelectedOptions: true,
			autocompleteValueFormatter: catSyncAutocompleteValueFormatter,
			autocompleteValueParser: catSyncAutocompleteValueParser,
			multiple: true,
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'MqttPubCommandPermissionTopic',
			header: 'PubCommandPermission',
			lookup: 'PubCommandPermissionTopic',
			type: 'string',
			filterSelectedOptions: true,
			autocompleteValueFormatter: catSyncAutocompleteValueFormatter,
			autocompleteValueParser: catSyncAutocompleteValueParser,
			multiple: true,
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ formik }) => formik.values.MqttPublishTopic?.includes?.('cmd')
		},

		{
			field: 'MqttQOS',
			label: 'QualityOfService',
			lookup: 'MqttQOS',
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'MqttSessionClean',
			label: 'MqttSessionClean',
			type: 'boolean',
			lookup: [
				{ LookupId: true, DisplayValue: 'Yes' },
				{ LookupId: false, DisplayValue: 'No' }
			],
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'MacAddress',
			label: 'MacAddress',
			type: 'string'
		},
		{
			field: 'payloadSize',
			label: 'payloadSize',
			type: 'number',
			minValue: 1,
			maxValue: 16,
			defaultValue: 5
		},
		{
			field: 'BrokerLogsCollectionPurgeDayCount',
			label: 'BrokerLogsCollectionPurgeDayCount',
			type: 'number',
			minValue: 1,
			defaultValue: 7
		},
		{
			field: 'MaxPublishCount',
			label: 'Maxpublishcount',
			type: 'number',
			minValue: 100,
			defaultValue: 240
		},
		{
			field: 'CATReconnection',
			label: 'CATReconnection',
			type: 'number',
			lookup: [
				{ LookupId: '1', DisplayValue: 'Yes' },
				{ LookupId: '0', DisplayValue: 'No' }
			],
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'CATReconnectionTime',
			header: 'CATReconnectionTime',
			type: 'number',
			inputWhen: ({ activeRecord, formik }) => {
				return [1, '1', true].includes(formik?.values?.CATReconnection);
			}
		},
		{
			field: 'CycleApproval',
			header: 'CycleReview',
			type: 'boolean',
			inlineLabelSize: 10,
			customizeInput: CustomSwitchInput,
			inputWrapperProps: {
				className: 'checkInput'
			}
		},
		{
			field: 'CycleApprovalStagesNote',
			header: 'SelectStagesToView',
			excludeOnSubmit: true,
			type: 'string',
			hideLabel: true,
			customizeInput: CycleConfigNote
		},
		{
			field: 'LeakTest',
			header: 'LEAKTEST',
			type: 'boolean',
			inlineLabelSize: 10,
			customizeInput: CustomSwitchInput,
			inputWrapperProps: {
				className: 'checkInput'
			}
		},
		{
			field: 'FlowCheck',
			header: 'FLOWCHECK',
			type: 'boolean',
			inlineLabelSize: 10,
			customizeInput: CustomSwitchInput,
			inputWrapperProps: {
				className: 'checkInput'
			}
		},
		{
			field: 'PreWash',
			header: 'PREWASH',
			type: 'boolean',
			inlineLabelSize: 10,
			customizeInput: CustomSwitchInput,
			inputWrapperProps: {
				className: 'checkInput'
			}
		},
		{
			field: 'Wash',
			header: 'WASH1',
			type: 'boolean',
			inlineLabelSize: 10,
			customizeInput: CustomSwitchInput,
			inputWrapperProps: {
				className: 'checkInput'
			}
		},
		{
			field: 'Rinse',
			header: 'RINSE1',
			type: 'boolean',
			inlineLabelSize: 10,
			customizeInput: CustomSwitchInput,
			inputWrapperProps: {
				className: 'checkInput'
			}
		},
		{
			field: 'Disinfection',
			header: 'CHEMDISINFECTION1',
			type: 'boolean',
			inlineLabelSize: 10,
			customizeInput: CustomSwitchInput,
			inputWrapperProps: {
				className: 'checkInput'
			}
		},
		{
			field: 'FinalRinse',
			header: 'FINALRINSE1',
			type: 'boolean',
			inlineLabelSize: 10,
			customizeInput: CustomSwitchInput,
			inputWrapperProps: {
				className: 'checkInput'
			}
		},
		{
			field: 'Name',
			header: 'Name'
		},
		{
			field: 'UploadURL',
			header: 'URL'
		},
		{
			field: 'ContentType',
			header: 'ContentType'
		},
		{
			field: 'AuthorizationToken',
			header: 'AuthorizationToken'
		},
		{
			field: 'CycleXMLFile',
			header: 'Form'
		},
		{
			field: 'CatService',
			header: 'CatService',
			readOnly: true
		},
		{
			field: 'ApiDbName',
			label: 'CatHost',
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('CatApi')
		},
		{
			field: 'CatFeature',
			label: 'CatFeature',
			lookup: 'CATAPIFeature',
			multiple: true,
			autocompleteValueFormatter: catSyncAutocompleteValueFormatter,
			autocompleteValueParser: catSyncAutocompleteValueParser,
			labelContainerProps: {
				sx: {
					height: 'fit-content'
				}
			},
			inputProps: {
				disableClearable: true
			},
			type: 'string',
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('CatApi')
		},
		{
			field: 'CatPort',
			label: 'CatPort',
			type: 'number',
			minValue: 0,
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('CatApi')
		},
		{
			field: 'IsCleanupService',
			label: 'CleanupServiceEnabled',
			type: 'boolean',
			lookup: [
				{ LookupId: true, DisplayValue: 'Yes' },
				{ LookupId: false, DisplayValue: 'No' }
			],
			autocompleteValueFormatter: (value) => value?.id,
			inputProps: {
				disableClearable: true
			},
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('CatApi')
		},
		{
			field: 'CleanupFrequency',
			label: 'CleanupFrequency',
			required: [['IsCleanupService', true]],
			inputWhen: [['IsCleanupService', true]]
		},
		{
			field: 'MaxCycleDays',
			label: 'CycleDays',
			required: [['IsCleanupService', true]],
			inputWhen: [['IsCleanupService', true]]
		},
		{
			field: 'APIArchiveLocation',
			label: 'ArchiveFolderPath',
			required: [['IsCleanupService', true]],
			inputWhen: [['IsCleanupService', true]]
		},
		{
			field: 'DbHost',
			label: 'DbHost',
			required: ({ activeRecord }) => !activeRecord?.IsDBConnected,
			disabled: ({ activeRecord }) => activeRecord?.IsDBConnected
		},
		{
			field: 'DbName',
			label: 'DbName',
			maxLength: ({ activeRecord }) =>
				activeRecord?.formGroupsVisibility?.get?.('DatabaseConfiguration')
					? 20
					: activeRecord?.record?.DbName?.length,
			required: ({ activeRecord }) => !activeRecord?.IsDBConnected,
			disabled: ({ activeRecord }) => activeRecord?.IsDBConnected
		},
		{
			field: 'DbUserName',
			label: 'DbUserName',
			disabled: ({ activeRecord }) => activeRecord?.IsDBConnected
		},
		{
			field: 'DbPassword',
			label: 'DbPassword',
			type: 'password',
			skipPasswordStrengthCheck: true,
			includeValueOnForm: true,
			disabled: ({ activeRecord }) => activeRecord?.IsDBConnected
		},
		{
			field: 'ConfirmDbPassword',
			label: 'ConfirmPassword',
			type: 'password',
			validate: ({ activeRecord }) =>
				yup
					.string()
					.test(
						'DbPassword-matches-ConfirmDbPassword',
						`${strings.PasswordDoesNotMatch || 'Password and Confirm Password does not Match'}`,
						(ConfirmDbPassword, { parent }) => {
							const DbPassword = parent?.DbPassword?.trim?.();
							if (!activeRecord?.IsDBConnected && DbPassword?.length > 0) {
								return DbPassword === ConfirmDbPassword;
							}
							return true;
						}
					),
			disabled: ({ activeRecord }) => activeRecord?.IsDBConnected
		},
		{
			field: 'TestDBConnectionBtn',
			label: 'TestApply',
			hideLabel: true,
			columnSize: 12,
			customizeInput: TestDbBtns
		}, //
		{
			field: 'CatCloudURL',
			header: 'URL',
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('CatCloudURL') && activeRecord?.formGroupsVisibility?.get?.('ScopeUpdate'),
			validate: ({ activeRecord }) => (activeRecord?.formGroupsVisibility?.get?.('CatCloudURL') ? 'isUrl' : undefined)
		}, //
		{
			field: 'FirstName',
			label: 'FirstName'
		},
		{
			field: 'LastName',
			label: 'LastName'
		},
		{
			field: 'UserName',
			label: 'Username',
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('UserCreation')
		},
		{
			field: 'Email',
			label: 'EmailAddress',
			validate: 'isEmail',
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('UserCreation')
		},
		{
			field: 'Password',
			label: 'Password',
			type: 'password',
			skipPasswordStrengthCheck: true,
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('UserCreation')
		},
		{
			field: 'ConfirmUserPassword',
			label: 'ConfirmPassword',
			type: 'password',
			validate: 'compare:Password',
			required: ({ activeRecord }) => activeRecord?.formGroupsVisibility?.get?.('UserCreation')
		},
		{
			field: 'UploadDiagnosticsUrl',
			label: 'UploadUrl',
			type: 'string',
			columnSize: 6,
			inlineLabelSize: 2
		},
		{
			field: 'UploadCyclesBtn',
			label: 'UploadCycles',
			hideLabel: true,
			columnSize: 2,
			customizeInput: ({ inputProps: { fieldLabel } }) => {
				return (
					<DiagnosticsBtn 
						action={'uploadCycleInfo'}
						fieldLabel={fieldLabel}
					/>
				);
			}
		},
		{
			field: 'UploadDiagnosticsBtn',
			label: 'UploadDiagnostics',
			hideLabel: true,
			columnSize: 2.3,
			customizeInput: ({ inputProps: { fieldLabel } }) => {
				return (
					<DiagnosticsBtn
						action={'uploadDiagnosticsInfo'}
						fieldLabel={fieldLabel}
					/>
				);
			}
		},
		{
			field: 'TrackCycleCollectionPurgeDayCount',
			label: 'TrackCycleCollectionPurgeDayCount',
			type: 'number',
			minValue: 1,
			defaultValue: 90
		},
		{
			field: 'AutoAuthorization',
			label: 'Autoauthorization',
			type: 'boolean',
			lookup: [
				{ id: true, label: 'Yes' },
				{ id: false, label: 'No' }
			],
			inputProps: {
				disableClearable: true
			}
		},
		{
			field: 'LicensingHostWsUrl',
			label: 'LicensehostURL',
			validate: 'isUrl',
			inputWhen: ({ activeRecord }) => {
				return activeRecord?.record?.showTestConnection;
			}
		},
		{
			field: 'TestConnection',
			label: 'TestConnection',
			hideLabel: true,
			customizeInput: TestConnectionBtn,
			inputWhen: ({ activeRecord }) => {
				return activeRecord?.IsDBConnected && activeRecord?.record?.showTestConnection;
			}
		},
		{
			field: 'VisionDataPurgeDayCount',
			label: 'Visiondatapurgedaycount',
			type: 'number',
			minValue: 1,
			defaultValue: 30
		},
		{
			field: 'RetryLoadingFilesBtn',
			label: 'RetryLoadingFilesBtn',
			hideLabel: true,
			columnSize: 3,
			customizeInput: RetryLoadingFilesBtn,
			RetryLoadingFilesBtn
		},
		{
			field: 'CurrentVersion',
			label: 'CurrentVersion',
			columnSize: 4,
			type:'string',
			inlineLabelSize: 6
		},
		{
			field: 'Path',
			label: 'Path',
			columnSize: 6,
			type: 'file',
			accept: '.json',
			inlineLabelSize: 4
		},
		{
			field: 'Upload',
			label: 'Upload',
			columnSize: 6,
			hideLabel: true,
			customizeInput : UploadPathBtn
		},
		{
			field: 'URL',
			label: 'URL',
			columnSize: 4,
			type:'string',
			inlineLabelSize: 6
		},
		{
			field: 'Time',
			label: 'Time',
			columnSize: 4,
			type:'string',
			inlineLabelSize: 6
		},
		{
			field: 'SyncInterval',
			lookup: 'SyncInterval',
			header: 'DownloadFileInterval',
			inputWhen: ({ activeRecord }) => {
				const { licenseType: { products = [] } } = activeRecord;
				return products.includes('catexport') && util.checkUserRoleAccess(['steris admin'] );
			}
		},
		{
			field: 'webServerMinTls',
			header: 'webServerMinTls',
			lookup: [
				{ LookupId: 1.3, DisplayValue: '1.3' }
			]
		},
		{
			field: 'AutoAuthorization',
			label: 'Autoauthorization',
			type: 'boolean',
			lookup: [
				{ LookupId: true, DisplayValue: 'Yes' },
				{ LookupId: false, DisplayValue: 'No' }
			],
			inputProps: {
				disableClearable: true
			},
			// onChange: ({ value, formik }) => {
			// 	if (!value && formik.values.AutoAuthorizeFromPayload) {
			// 		formik.setFieldValue('AutoAuthorizeFromPayload', false)
			// 	}
			// }
		},
		// {
		// 	field: 'AutoAuthorizeFromPayload',
		// 	label: 'AutoAuthorizeFromPayload',
		// 	type: 'boolean',
		// 	lookup: [
		// 		{ id: true, label: 'Yes' },
		// 		{ id: false, label: 'No' }
		// 	],
		// 	inputProps: {
		// 		disableClearable: true
		// 	},
		// 	disabled: ({ formik }) => !formik?.values?.AutoAuthorization 
		// }
		{
			field: 'mqttCipherValues',
			label: 'mqttCipherValues',
			type: 'string',
			inputWhen: ()=>util.checkUserRoleAccess(['steris admin'])
		},
		{
			field: 'ApiLogLevel',
			header: 'ApiLogLevel',
			lookup: 'ApiLogLevel'
		},
		{ 
			field: 'ApiLogPath',
			header: 'ApiLogPath',
			columnSize: 4,
			type: 'string'
		},
		{
			field: 'DiagnosticNoOfDays',
			label: 'DiagnosticNoOfDays',
			columnSize: 6,
			inlineLabelSize: 2,
			type: 'number',
			minValue: 1,
			maxValue: 14,
			defaultValue: 2
		},
		{
			field: 'SaveToLocalStorage',
			label: 'SaveToLocalStorage',
			hideLabel: true,
			columnSize: 3,
			customizeInput: ({ inputProps: { fieldLabel } }) => {
				return (
					<DiagnosticsBtn
						action={'saveToLocalStorage'}
						fieldLabel={fieldLabel}
					/>
				);
			}
		},
		{
			field: "FailSafeRestartMode",
			label: "FailSafeRestartMode",
			lookup:"FailSafeRestartModes",
			type: 'number',
			autocompleteValueParser: (value)=> value.toString(),
			inputProps: {
				disableClearable: true
			},
			inputWhen: ({ formik }) => {
				return [1, '1', true].includes(formik?.values?.CATReconnection);
			}
		}
	]
});

export default model.createFormDialog();
